import { Money } from '@libs/money/money.interfaces';
import { StatsigEvent } from '@/tracking/statsig/types';
import { eventIsTracked } from '@/tracking/statsig/utils';
import { pushStatsig } from '@/tracking/statsig/index';
import { AnyProduct } from '@shared/interfaces/product';
import { Message } from '@/components/paymentPage/Message/Message.interfaces';
import { ProductType } from '@/api/Cart/cart.interfaces';
import { mapProductsAsItems } from '@/tracking/analytics/Checkout/Helpers/map';
import { BookNowPayLaterType } from '@/components/shared/BookNowPayLater/BookNowPayLater.interfaces';

const getProductsMetaData = (anyProducts: AnyProduct[]) => {
    const products = anyProducts.map((p) => p.$type);

    return {
        count_products_applied_discount: getProductsHasAppliedDiscount(anyProducts),
        products,
        ...getProductsType(anyProducts),
        ...getProductCountByType(anyProducts),
    };
};

const getProductCountByType = (productArray: AnyProduct[]): Record<string, number> => {
    return productArray.reduce(
        (acc, product) => {
            const type = product.$type;
            acc[type] = (acc[type] || 0) + 1;
            return acc;
        },
        {} as Record<string, number>
    );
};

const getProductsType = (productArray: AnyProduct[]): Record<string, number> => {
    return productArray.reduce(
        (acc, product) => {
            const type =
                product.Type === ProductType.Addon
                    ? `count_${product.$type?.toLowerCase()}`
                    : `count_${product.Type?.toLowerCase()}`;

            acc[type] = (acc[type] || 0) + 1;
            return acc;
        },
        {} as Record<string, number>
    );
};

const getProductsHasAppliedDiscount = (products: AnyProduct[]) => {
    return products.filter(
        (x) =>
            x.AppliedDiscount &&
            x.AppliedDiscount.Name &&
            x.AppliedDiscount.Amount?.Amount > 0
    ).length;
};

export const logStatsigPaymentPageInitiated = (
    productArray: AnyProduct[],
    totalToPay: Money,
    bookingId: string
) => {
    if (!eventIsTracked(StatsigEvent.PAYMENT_PAGE_INITIATED)) {
        const metadata = {
            currency: totalToPay.Currency,
            value: totalToPay.Amount.toString(),
            bookingId,
            ...getProductsMetaData(productArray),
        };

        pushStatsig({
            eventName: StatsigEvent.PAYMENT_PAGE_INITIATED,
            metadata,
        });
    }
};

export const logStatsigPaymentData = (
    productArray: AnyProduct[],
    totalToPay: Money,
    bookingId: string
) => {
    if (!eventIsTracked(StatsigEvent.PAYMENT_METHOD_COMPLETED)) {
        const metadata = {
            currency: totalToPay.Currency,
            value: totalToPay.Amount.toString(),
            bookingId,
            ...getProductsMetaData(productArray),
        };

        pushStatsig({
            eventName: StatsigEvent.PAYMENT_METHOD_COMPLETED,
            metadata,
        });
    }
};

export const logStatsigPaymentTypeSelect = (paymentType: string) => {
    const metadata = { paymentType };

    pushStatsig({
        eventName: StatsigEvent.PAYMENT_TYPE_SELECT,
        metadata,
    });
};

export const logStatsigPaymentMethodSelect = (
    paymentMethod: string,
    paymentProvider?: string
) => {
    const metadata = { paymentMethod, paymentProvider };

    pushStatsig({
        eventName: StatsigEvent.PAYMENT_METHOD_SELECT,
        metadata,
    });
};

export const logStatsigGiftCardRequiresCvc = () => {
    const metadata = {};

    pushStatsig({
        eventName: StatsigEvent.PAYMENT_GIFT_CARD_REQUIRES_CVC,
        metadata,
    });
};

export const logStatsigPaymentCancellationTermsClick = () => {
    if (!eventIsTracked(StatsigEvent.PAYMENT_CANCELLATION_TERMS_CLICK)) {
        pushStatsig({
            eventName: StatsigEvent.PAYMENT_CANCELLATION_TERMS_CLICK,
        });
    }
};

export const logStatsigPaymentFailedError = (message: string) => {
    const metadata = {
        error_message: message,
    };

    pushStatsig({
        eventName: StatsigEvent.PAYMENT_FAILED_ERROR,
        metadata,
    });
};

export const logStatsigPaymentPageMessage = (message: Message) => {
    const metadata = {
        message_type: message.type,
    };

    pushStatsig({
        eventName: StatsigEvent.PAYMENT_PAGE_MESSAGE,
        metadata,
    });
};

export const logStatsigPaymentBookingRedirect = (products: AnyProduct[]) => {
    const metadata = {
        items: mapProductsAsItems(products),
        ...getProductsType(products),
    };

    pushStatsig({
        eventName: StatsigEvent.PAYMENT_BOOKING_REDIRECT,
        metadata,
    });
};

export const logStatsigPaymentLeabankFormOpen = () => {
    if (!eventIsTracked(StatsigEvent.PAYMENT_LEABANK_FORM_OPEN)) {
        pushStatsig({
            eventName: StatsigEvent.PAYMENT_LEABANK_FORM_OPEN,
        });
    }
};

export const logStatsigLeabankSubmitApplication = () => {
    if (!eventIsTracked(StatsigEvent.PAYMENT_LEABANK_SUBMIT_APPLICATION)) {
        pushStatsig({
            eventName: StatsigEvent.PAYMENT_LEABANK_SUBMIT_APPLICATION,
        });
    }
};

export const logStatsigBookNowPayLaterAvailable = (type: BookNowPayLaterType) => {
    const metadata = {
        paymentMethod: type,
    };

    pushStatsig({
        eventName: StatsigEvent.PAYMENT_BOOK_NOW_PAY_LATER_AVAILABLE,
        metadata,
    });
};

export const logStatsigBookNowPayLaterInteraction = (
    type: BookNowPayLaterType | undefined,
    interactionType: 'ReadMore'
) => {
    const metadata = {
        paymentMethod: type,
        interactionType,
    };

    pushStatsig({
        eventName: StatsigEvent.PAYMENT_BOOK_NOW_PAY_LATER_INTERACTION,
        metadata,
    });
};
